/**
 * JSON-LD (schema.org) builder utilities.
 * Pure functions that assemble structured data objects from page/product data.
 */

import type { JsonLdComposition, ResolvedSeoMeta } from "@/types/seo";

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Base schema.org context */
const SCHEMA_CONTEXT = "https://schema.org";

export interface JsonLdProductData {
  name: string;
  description?: string;
  image?: string;
  sku?: string;
  price: string;
  currency: string;
  availability?: "InStock" | "OutOfStock" | "PreOrder";
  brand?: string;
}

export interface JsonLdBreadcrumb {
  name: string;
  url: string;
}

/**
 * Build the appropriate JSON-LD schema based on the composition's schemaType.
 */
export function buildJsonLdSchema(
  composition: JsonLdComposition | undefined,
  seo: ResolvedSeoMeta,
  options?: {
    productData?: JsonLdProductData;
    breadcrumbs?: JsonLdBreadcrumb[];
  }
): Record<string, any>[] {
  if (!composition) return [];

  const schemas: Record<string, any>[] = [];
  const schemaType = composition.schemaType || "WebPage";

  switch (schemaType) {
    case "Product":
      if (options?.productData) {
        schemas.push(buildProductSchema(composition, seo, options.productData));
      }
      break;
    case "Article":
      schemas.push(buildArticleSchema(composition, seo));
      break;
    case "Organization":
      schemas.push(buildOrganizationSchema());
      break;
    case "WebPage":
    default:
      schemas.push(buildWebPageSchema(composition, seo));
      break;
  }

  // Always add breadcrumbs if available
  if (options?.breadcrumbs?.length) {
    schemas.push(buildBreadcrumbListSchema(options.breadcrumbs));
  }

  return schemas;
}

export function buildWebPageSchema(
  composition: JsonLdComposition,
  seo: ResolvedSeoMeta
): Record<string, any> {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "WebPage",
    name: composition.customName || seo.og.title || seo.title,
    description:
      composition.customDescription || seo.og.description || seo.description,
    url: seo.og.url,
    ...(seo.og.image && { image: seo.og.image }),
    ...(seo.og.locale && { inLanguage: seo.og.locale }),
  };
}

export function buildProductSchema(
  composition: JsonLdComposition,
  seo: ResolvedSeoMeta,
  product: JsonLdProductData
): Record<string, any> {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Product",
    name: composition.customName || product.name,
    description:
      composition.customDescription ||
      product.description ||
      seo.og.description ||
      seo.description,
    ...(product.image && { image: product.image }),
    ...(seo.og.url && { url: seo.og.url }),
    ...(product.sku && { sku: product.sku }),
    ...(product.brand && {
      brand: { "@type": "Brand", name: product.brand },
    }),
    offers: {
      "@type": "Offer",
      price: product.price,
      priceCurrency: product.currency,
      availability: `https://schema.org/${product.availability ?? "InStock"}`,
      url: seo.og.url,
    },
  };
}

export function buildArticleSchema(
  composition: JsonLdComposition,
  seo: ResolvedSeoMeta
): Record<string, any> {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Article",
    headline: composition.customName || seo.og.title || seo.title,
    description:
      composition.customDescription || seo.og.description || seo.description,
    url: seo.og.url,
    ...(seo.og.image && { image: seo.og.image }),
    ...(seo.og.locale && { inLanguage: seo.og.locale }),
    ...(seo.article?.publishedTime && {
      datePublished: seo.article.publishedTime,
    }),
    publisher: {
      "@type": "Organization",
      name: "WeHunt",
    },
  };
}

export function buildOrganizationSchema(): Record<string, any> {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Organization",
    name: "WeHunt",
    url: "https://www.wehunt.com",
  };
}

export function buildBreadcrumbListSchema(
  breadcrumbs: JsonLdBreadcrumb[]
): Record<string, any> {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}
